import { z } from 'zod';
/**
 * The rate Jacky sets on a proposal, and the ONE place its arithmetic lives.
 *
 * Jacky, 17 September, describing his model: *"I setup a GM rate or Markup %, all other product
 * selected by customer list by that rate"* — and then, asked which number he means:
 *
 *   **"GM % of the SELL price — 40% GM means sell = cost ÷ 0.6 (a 66.7% markup on cost)"**
 *
 * That distinction is not pedantry, it is 19% of the price: the book prices on a 40% markup ON COST
 * (`sell = cost × 1.4`), which is only a 28.6% gross margin. Read as margin, the same instruction
 * sells at `cost ÷ 0.6 = 1.667 ×`. Both are legitimate; they are different money, so the kind is
 * carried explicitly and the screen shows both readings rather than assuming one.
 *
 * GM is a share of the SELL price (`(sell − cost) / sell`); markup is a factor on the COST
 * (`(sell − cost) / cost`). The conversion is exact and lives here — one implementation, tested,
 * used by the API when it prices an options pool and by the builder when it labels the control.
 */
export const Margin = z.object({
    kind: z.enum(['gm', 'markup']),
    /** A fraction, not a percentage: 0.4 is "40%". Bounded below 1 because a 100% GM has no sell price. */
    value: z.number().finite().min(0).max(0.95),
});
export const DEFAULT_MARGIN = { kind: 'gm', value: 0.4 };
/** Cost → sell. The whole point of the rate. */
export function sellAt(cost, margin) {
    if (margin.kind === 'markup')
        return cost * (1 + margin.value);
    return cost / (1 - margin.value);
}
/** The same rate said the other way: 40% GM of sell is a 66.7% markup on cost. */
export function markupFromMargin(margin) {
    return margin.kind === 'markup' ? margin.value : margin.value / (1 - margin.value);
}
/** And back: the book's 40% markup on cost is a 28.6% gross margin. */
export function marginFromMarkup(markup) {
    return { kind: 'gm', value: markup / (1 + markup) };
}
/** Both readings, for a control that must not be ambiguous. */
export function marginReadings(margin) {
    return margin.kind === 'gm' ? { gm: margin.value, markup: markupFromMargin(margin) } : { gm: marginFromMarkup(margin.value).value, markup: margin.value };
}
const pct = (n) => `${Math.round(n * 1000) / 10}%`;
/** "GM 40% of sell (markup 66.7% on cost)" — the sentence that removes the ambiguity. */
export function marginLabel(margin) {
    const { gm, markup } = marginReadings(margin);
    return margin.kind === 'gm'
        ? `GM ${pct(gm)} of sell (markup ${pct(markup)} on cost)`
        : `Markup ${pct(markup)} on cost (GM ${pct(gm)} of sell)`;
}
/**
 * A line's price at the rate, rounded to cents.
 *
 * `cost` is the engine's own basis for that line — its material plus its labour, which is exactly what
 * the engine multiplies by its markup. Nothing here invents a cost: a line the engine could not cost
 * (`null`) returns null, so an option with an unknown basis reads as unpriced rather than as a guess.
 */
export function sellForCost(cost, margin) {
    if (cost === null || !Number.isFinite(cost) || cost < 0)
        return null;
    return Math.round(sellAt(cost, margin) * 100) / 100;
}
/**
 * What an option line sells for at the rate: the engine's cost basis times the rate.
 *
 * Returns null when the engine could not cost the line — a missing glue or labour rate, an unmeasured
 * area — because a price built on an unknown cost is the one thing a quote must never carry.
 */
export function optionSell(line, margin) {
    if (line.material === null || line.labour === null)
        return null;
    return sellForCost(line.material + line.labour, margin);
}
/** GM as a share of the SELL price — the same meaning he chose for the rate, so the two are comparable. */
export function gmPercent(sell, contribution) {
    if (sell === null || contribution === null || !(sell > 0))
        return null;
    return Math.round((contribution / sell) * 10000) / 10000;
}
export function gmScenarios(sell, internal) {
    const external = { scenario: 'external_install', sell, contribution: internal.externalContribution, gm: gmPercent(sell, internal.externalContribution) };
    const artemisCrew = { scenario: 'artemis_crew', sell, contribution: internal.artemisContribution, gm: gmPercent(sell, internal.artemisContribution) };
    const bonus = external.gm === null || artemisCrew.gm === null ? null : Math.round((artemisCrew.gm - external.gm) * 10000) / 10000;
    return { external, artemisCrew, bonus };
}
/** "42.0%" — one place formats it, so no screen invents its own. */
export function gmLabel(gm) {
    return gm === null ? '—' : `${Math.round(gm * 1000) / 10}%`;
}
//# sourceMappingURL=margin.js.map